import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useValidatedForm } from "@/components/app/FormKit";
import { ThemePicker, ProductStep, StepProgress, STEP_ICONS, validateProduct, useThemes } from "@/components/onboarding/parts";
import { btnOutline, btnPrimary } from "@/components/app/ui";
import "@/pages/landing.css";

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const themes = useThemes();
  const [store, setStore] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [saving, setSaving] = useState(false);

  const form = useValidatedForm({ name: "", price: "", images: [] }, validateProduct);

  useEffect(() => {
    api.get("/vendor/store").then(({ data }) => { setStore(data); setSelectedTheme(data.theme); });
  }, []);

  const applyTheme = async (t) => {
    setSelectedTheme(t);
    try { await api.put("/vendor/store/theme", t); toast.success(`${t.name} theme applied to your store.`); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const finish = async () => {
    if (!form.check()) return;
    const { name, price, images } = form.values;
    setSaving(true);
    try {
      if (name.trim() && price) {
        await api.post("/vendor/products", {
          name: name.trim(), price: parseFloat(price), image: images[0] || "", images,
          stock: 10, status: "active", featured: true, category: store?.business_category || "General",
        });
        toast.success(`“${name.trim()}” added to your catalog.`);
      }
      await api.put("/vendor/store", { onboarding_complete: true });
      toast.success("You're all set! Welcome to your dashboard.");
      nav("/vendor");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="lp min-h-screen bg-gradient-to-b from-blue-50/70 via-white to-white font-['Plus_Jakarta_Sans',Inter,sans-serif] relative overflow-hidden">
      <div className="lp-blob absolute -right-24 top-10 w-[480px] h-[480px] rounded-full bg-blue-200/50 blur-3xl" />
      <div className="lp-blob absolute -left-32 top-96 w-80 h-80 rounded-full bg-orange-100/60 blur-3xl" />

      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/"><img src="/logo-light.png" alt="sell24x7.com" className="h-9 w-auto" /></Link>
          <button onClick={() => nav("/vendor")} className="lp-link text-[13px] font-semibold text-slate-600" data-testid="onboarding-skip">Skip for now</button>
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-6 py-8">
        <div className="mb-7"><StepProgress step={step} steps={[{ label: "Store", icon: STEP_ICONS.store }, { label: "Theme", icon: STEP_ICONS.theme }, { label: "Product", icon: STEP_ICONS.product }]} /></div>

        <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="rounded-xl bg-white border border-slate-200 shadow-[0_20px_60px_rgba(37,99,235,0.10)] p-6">
          {step === 1 && store && (
            <div className="text-center py-4">
              <h1 className="font-display text-2xl font-extrabold text-neutral-900">Welcome, {store.name} 🎉</h1>
              <p className="text-slate-500 text-[13px] mt-2">Your storefront is live at</p>
              <div className="mt-2.5 inline-block font-mono text-[13px] text-orange-600 bg-orange-50 px-4 py-2 rounded-lg border border-orange-100">
                sell24x7.com/store/{store.slug}
              </div>
              <p className="text-slate-500 text-[13px] mt-5 max-w-md mx-auto">Let's pick a theme and add your first product to get selling.</p>
            </div>
          )}

          {step === 2 && <ThemePicker themes={themes} category={store?.business_category} selected={selectedTheme} onPick={applyTheme} />}

          {step === 3 && <ProductStep form={form} />}

          <div className="flex justify-between items-center mt-6">
            {step > 1 ? <button onClick={() => setStep((s) => s - 1)} className={btnOutline}>Back</button> : <span />}
            {step < 3 ? (
              <button onClick={() => setStep((s) => s + 1)} data-testid="onboarding-next" className={btnPrimary}>Continue <ArrowRight size={14} /></button>
            ) : (
              <button onClick={finish} disabled={saving} data-testid="onboarding-finish" className={btnPrimary}>{saving ? "Finishing…" : "Go to dashboard"}</button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
